
import React, { useState } from 'react';
import { Star, TrendingUp, TrendingDown, List, Filter, ArrowUpDown, Download, SmilePlus } from 'lucide-react';

const StationPerformanceTable = ({ 
  stationData = [], 
  favoriteStations = [], 
  handleToggleFavorite = () => {}, 
  customerSatisfaction = {} 
}) => {
  const [sortField, setSortField] = useState('volume');
  const [sortDirection, setSortDirection] = useState('desc');
  const [visibleColumns, setVisibleColumns] = useState({
    volume: true,
    premium: true,
    margin: true,
    cafe: true,
    carcare: true,
    clv: true,
    repeat: true,
    satisfaction: true,
    status: true
  });
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  
  // Handle sort changes
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  // Toggle column visibility
  const toggleColumn = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };
  
  // Make sure stationData is an array before attempting to sort it
  const dataToRender = Array.isArray(stationData) ? stationData : [];
  
  // Sort stations
  const sortedStations = [...dataToRender].sort((a, b) => {
    if (!a || !b || a[sortField] === undefined || b[sortField] === undefined) {
      return 0;
    }
    
    if (sortDirection === 'asc') {
      return a[sortField] > b[sortField] ? 1 : -1;
    } else {
      return a[sortField] < b[sortField] ? 1 : -1;
    }
  });
  
  // Helper to format statuses with icons and appropriate colors
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'above':
        return <span className="flex items-center text-green-600"><TrendingUp size={14} className="mr-1" /> Above Target</span>;
      case 'below':
        return <span className="flex items-center text-red-600"><TrendingDown size={14} className="mr-1" /> Below Target</span>;
      default:
        return <span className="flex items-center text-blue-600">On Target</span>;
    }
  };
  
  // Handle export data
  const handleExportData = () => {
    // In a real app, this would generate a CSV download
    alert('Exporting station data as CSV');
  };
  
  // Calculate if a value is good (green), neutral, or bad (red)
  const getValueColor = (value, metric) => {
    const thresholds = {
      volume: { good: 4500, neutral: 3500 },
      premium: { good: 35, neutral: 25 },
      margin: { good: 0.85, neutral: 0.75 },
      cafe: { good: 250, neutral: 175 },
      carcare: { good: 15000, neutral: 10000 },
      clv: { good: 8.0, neutral: 7.0 },
      repeat: { good: 40, neutral: 30 },
      satisfaction: { good: 85, neutral: 80 }
    };
    
    const threshold = thresholds[metric];
    if (!threshold) return 'text-gray-900';
    
    if (value >= threshold.good) return 'text-green-600';
    if (value >= threshold.neutral) return 'text-gray-900';
    return 'text-red-600';
  };

  // Get satisfaction score for a station based on segment
  const getStationSatisfaction = (station) => {
    if (!station || !station.id) return 80; // Default value
    
    // Extract segment from station ID prefix
    let segment = 'urban'; // default
    if (station.id.startsWith('URB')) segment = 'urban';
    else if (station.id.startsWith('HWY')) segment = 'highway';
    else if (station.id.startsWith('RUR')) segment = 'rural';
    else if (station.id.startsWith('SUB')) segment = 'suburban';
    
    // Return satisfaction score for the segment
    return (customerSatisfaction && customerSatisfaction[segment]) || 80;
  };
  
  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center text-lg font-medium text-gray-900">
            <List size={20} className="mr-2 text-purple-600" />
            Station Performance
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="relative">
              <button 
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 flex items-center hover:bg-gray-50"
                onClick={() => setShowColumnSelector(!showColumnSelector)}
              >
                <Filter size={14} className="mr-1" />
                Columns
              </button>
              
              {showColumnSelector && (
                <div className="absolute top-full right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                  <div className="p-2 border-b border-gray-200">
                    <h3 className="text-xs font-medium text-gray-700">Toggle Columns</h3>
                  </div>
                  <div className="p-2 space-y-2">
                    <label className="flex items-center text-sm">
                      <input 
                        type="checkbox" 
                        checked={visibleColumns.volume} 
                        onChange={() => toggleColumn('volume')}
                        className="mr-2"
                      />
                      Volume
                    </label>
                    <label className="flex items-center text-sm">
                      <input 
                        type="checkbox" 
                        checked={visibleColumns.premium} 
                        onChange={() => toggleColumn('premium')}
                        className="mr-2"
                      />
                      Premium %
                    </label>
                    <label className="flex items-center text-sm">
                      <input 
                        type="checkbox" 
                        checked={visibleColumns.margin} 
                        onChange={() => toggleColumn('margin')}
                        className="mr-2"
                      />
                      Margin
                    </label>
                    <label className="flex items-center text-sm">
                      <input 
                        type="checkbox" 
                        checked={visibleColumns.cafe} 
                        onChange={() => toggleColumn('cafe')}
                        className="mr-2"
                      />
                      Café Sales
                    </label>
                    <label className="flex items-center text-sm">
                      <input 
                        type="checkbox" 
                        checked={visibleColumns.carcare} 
                        onChange={() => toggleColumn('carcare')}
                        className="mr-2"
                      />
                      Car Care
                    </label>
                    <label className="flex items-center text-sm">
                      <input 
                        type="checkbox" 
                        checked={visibleColumns.clv} 
                        onChange={() => toggleColumn('clv')}
                        className="mr-2"
                      />
                      CLV
                    </label>
                    <label className="flex items-center text-sm">
                      <input 
                        type="checkbox" 
                        checked={visibleColumns.repeat} 
                        onChange={() => toggleColumn('repeat')}
                        className="mr-2"
                      />
                      Repeat %
                    </label>
                    <label className="flex items-center text-sm">
                      <input 
                        type="checkbox" 
                        checked={visibleColumns.satisfaction} 
                        onChange={() => toggleColumn('satisfaction')}
                        className="mr-2"
                      />
                      Satisfaction
                    </label>
                    <label className="flex items-center text-sm">
                      <input 
                        type="checkbox" 
                        checked={visibleColumns.status} 
                        onChange={() => toggleColumn('status')}
                        className="mr-2"
                      />
                      Status
                    </label>
                  </div>
                </div>
              )}
            </div>
            
            <button 
              className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 flex items-center hover:bg-gray-50"
              onClick={handleExportData}
            >
              <Download size={14} className="mr-1" />
              Export
            </button>
          </div>
        </div>
      </div>
      
      <div className="p-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Station
              </th>
              
              {visibleColumns.volume && (
                <th 
                  className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group"
                  onClick={() => handleSort('volume')}
                >
                  <div className="flex items-center">
                    Volume
                    <ArrowUpDown size={14} className={`ml-1 transition-colors ${sortField === 'volume' ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-600'}`} />
                  </div>
                </th>
              )}
              
              {visibleColumns.premium && (
                <th 
                  className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group"
                  onClick={() => handleSort('premium')}
                >
                  <div className="flex items-center">
                    Premium %
                    <ArrowUpDown size={14} className={`ml-1 transition-colors ${sortField === 'premium' ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-600'}`} />
                  </div>
                </th>
              )}
              
              {visibleColumns.margin && (
                <th 
                  className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group"
                  onClick={() => handleSort('margin')}
                >
                  <div className="flex items-center">
                    Margin
                    <ArrowUpDown size={14} className={`ml-1 transition-colors ${sortField === 'margin' ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-600'}`} />
                  </div>
                </th>
              )}
              
              {visibleColumns.cafe && (
                <th 
                  className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group"
                  onClick={() => handleSort('cafe')}
                >
                  <div className="flex items-center">
                    Café Sales
                    <ArrowUpDown size={14} className={`ml-1 transition-colors ${sortField === 'cafe' ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-600'}`} />
                  </div>
                </th>
              )}
              
              {visibleColumns.carcare && (
                <th 
                  className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group"
                  onClick={() => handleSort('carcare')}
                >
                  <div className="flex items-center">
                    Car Care
                    <ArrowUpDown size={14} className={`ml-1 transition-colors ${sortField === 'carcare' ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-600'}`} />
                  </div>
                </th>
              )}
              
              {visibleColumns.clv && (
                <th 
                  className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group"
                  onClick={() => handleSort('clv')}
                >
                  <div className="flex items-center">
                    CLV
                    <ArrowUpDown size={14} className={`ml-1 transition-colors ${sortField === 'clv' ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-600'}`} />
                  </div>
                </th>
              )}
              
              {visibleColumns.repeat && (
                <th 
                  className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group"
                  onClick={() => handleSort('loyalty')}
                >
                  <div className="flex items-center">
                    Repeat %
                    <ArrowUpDown size={14} className={`ml-1 transition-colors ${sortField === 'loyalty' ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-600'}`} />
                  </div>
                </th>
              )}
              
              {visibleColumns.satisfaction && (
                <th 
                  className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  <div className="flex items-center">
                    <SmilePlus size={14} className="mr-1 text-purple-500" />
                    Satisfaction
                  </div>
                </th>
              )}
              
              {visibleColumns.status && (
                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedStations.map((station, index) => (
              <tr key={station.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center">
                      {favoriteStations && favoriteStations.includes(station.id) ? (
                        <Star
                          size={18}
                          fill="currentColor"
                          className="text-yellow-500 cursor-pointer"
                          onClick={() => handleToggleFavorite && handleToggleFavorite(station.id)}
                        />
                      ) : (
                        <Star
                          size={18}
                          className="text-gray-300 cursor-pointer hover:text-yellow-500"
                          onClick={() => handleToggleFavorite && handleToggleFavorite(station.id)}
                        />
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{station.name}</div>
                      <div className="text-sm text-gray-500">{station.id}</div>
                    </div>
                  </div>
                </td>
                
                {visibleColumns.volume && (
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`text-sm ${getValueColor(station.volume, 'volume')}`}>
                      {station.volume.toLocaleString()} L/day
                    </div>
                  </td>
                )}
                
                {visibleColumns.premium && (
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`text-sm ${getValueColor(station.premium, 'premium')}`}>
                      {station.premium}%
                    </div>
                  </td>
                )}
                
                {visibleColumns.margin && (
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`text-sm ${getValueColor(station.margin, 'margin')}`}>
                      {station.margin.toFixed(2)} THB/L
                    </div>
                  </td>
                )}
                
                {visibleColumns.cafe && (
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`text-sm ${getValueColor(station.cafe, 'cafe')}`}>
                      {station.cafe} cups/day
                    </div>
                  </td>
                )}
                
                {visibleColumns.carcare && (
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`text-sm ${getValueColor(station.carcare || (station.volume * 2.5), 'carcare')}`}>
                      {(station.carcare || Math.round(station.volume * 2.5)).toLocaleString()} THB/day
                    </div>
                  </td>
                )}
                
                {visibleColumns.clv && (
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`text-sm ${getValueColor(station.clv, 'clv')}`}>
                      {station.clv.toFixed(1)}K THB
                    </div>
                  </td>
                )}
                
                {visibleColumns.repeat && (
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`text-sm ${getValueColor(station.loyalty, 'repeat')}`}>
                      {station.loyalty}%
                    </div>
                  </td>
                )}
                
                {visibleColumns.satisfaction && (
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`text-sm flex items-center ${getValueColor(getStationSatisfaction(station), 'satisfaction')}`}>
                      {getStationSatisfaction(station)}%
                    </div>
                  </td>
                )}
                
                {visibleColumns.status && (
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm">{getStatusDisplay(station.status)}</div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StationPerformanceTable;
